import { memo, useCallback, useMemo } from "react"
import { Box, IconButton, ListItem, Tooltip, useMediaQuery } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import SpeedIcon from "@material-ui/icons/Speed"
import VisibilityIcon from "@material-ui/icons/Visibility"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import WifiIcon from "@mui/icons-material/Wifi"
import {
  buildStateIntegrityBadgeStyle,
  stateTreeActionButtonStyle,
  stateTreeBadgeStyle,
  stateTreeCardBaseStyle,
  stateTreeGuideColor,
  themePalette,
} from "./stateTreeStyles"

const SELECTED_NODE_BACKGROUND = "#e0f2fe"
const TREE_INDENT_SIZE = 20
const TREE_GUIDE_BLEED = 3
const TREE_GUIDE_ANCHOR_X = 12
const TREE_GUIDE_ANCHOR_X_COMPACT = 10
const HIGH_INTEGRITY_THRESHOLD = 80
const MEDIUM_INTEGRITY_THRESHOLD = 50
const REGULAR_INTEGRITY_COLUMN_WIDTH = "5rem"
const COMPACT_INTEGRITY_COLUMN_WIDTH = "3.35rem"
const REGULAR_ICON_COLUMN_WIDTH = "1rem"
const COMPACT_ICON_COLUMN_WIDTH = "1.22rem"

export const getStateTreeRowHeight = ({ depth, isCompact }) => {
  const baseHeight = 48
  const compactAdjustment = isCompact ? 0 : 2
  return baseHeight + compactAdjustment
}

const normalizeNodeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

const getNodeDisplayName = (node) => node?.nombre || node?.device_info?.device_name || "-"

const formatHierarchyBadgeLabel = (value) =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const getNodeBadgeLabel = (node) => {
  if (node?.level_nombre) return formatHierarchyBadgeLabel(node.level_nombre)
  if (node?.type === "automate") return "Automate"
  if (node?.producto || node?.product) return formatHierarchyBadgeLabel(node.producto || node.product)
  return "Nodo"
}

const shouldDisableNodeSelection = (node, fromStateDashboard) =>
  !fromStateDashboard &&
  (!node?.medidor && !(node?.automates?.length > 0)) &&
  node?.type !== "automate"

const parseIntegrityValue = (value) => {
  const normalizedValue = String(value ?? "")
    .replace("%", "")
    .replace(",", ".")
    .trim()
  const parsedValue = parseFloat(normalizedValue)

  return Number.isFinite(parsedValue) ? Math.min(100, Math.max(0, parsedValue)) : null
}

const resolveNodeIntegrityValue = (nodeStatus, node) =>
  nodeStatus?.stats?.integridad ||
  node?.stats?.integridad ||
  nodeStatus?.powermate?.data_integrity ||
  node?.powermate?.data_integrity ||
  null

const getIntegrityTone = (integrityValue) => {
  if (integrityValue === null) return "neutral"
  if (integrityValue >= HIGH_INTEGRITY_THRESHOLD) return "success"
  if (integrityValue >= MEDIUM_INTEGRITY_THRESHOLD) return "warning"
  return "danger"
}

const formatIntegrityLabel = (integrityValue) => {
  if (integrityValue === null) return null
  return `${Math.round(integrityValue)}%`
}

const getNodeRowStyle = ({ isSelected, isDisabled, depth }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  padding: depth === 1? "0.42rem 0.75rem" : "0.36rem 0.72rem",
  minHeight: "48px",
  cursor: isDisabled ? "default" : "pointer",
  backgroundColor: isSelected ? SELECTED_NODE_BACKGROUND : themePalette.common.white,
  opacity: isDisabled ? 0.64 : 1,
  boxSizing: "border-box",
})

const getNodeItemStyle = ({ isSelected, depth, hasChildren }) => ({
  ...stateTreeCardBaseStyle,
  boxShadow: depth === 0 ? themePalette.shadow.card : "none",
  borderColor: themePalette.border.subtle,
  backgroundColor: isSelected
    ? SELECTED_NODE_BACKGROUND
    : hasChildren && depth > 0
      ? themePalette.surface.soft
      : themePalette.common.white,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
})

function StateTreeRow({
  item,
  selectedNodeId,
  handleSelectedNode,
  statusByNodeId,
  statusNodesByNodeId,
  fromStateDashboard = true,
  handleDeleteNode,
  isCompact,
  onToggleExpanded,
}) {
  const { node, depth, hasChildren, isExpanded, hasNextSibling, ancestorHasNext } = item
  const isSelected = selectedNodeId === node?.id
  const isDisabled = shouldDisableNodeSelection(node, fromStateDashboard)
  const hideHierarchyBadge = useMediaQuery("(max-width:600px)")
  const levelName = useMemo(() => normalizeNodeText(node?.level_nombre), [node?.level_nombre])
  const nodeLabel = useMemo(() => getNodeDisplayName(node), [node])
  const badgeLabel = useMemo(() => getNodeBadgeLabel(node), [node])
  const nodeStatus = statusByNodeId?.[node?.id] || node
  const nodeConnectivity = statusNodesByNodeId?.[node?.id]
  const statusNode = node?.virtual ? undefined : nodeConnectivity?.online
  const hasAutomate = Boolean(node?.automates?.length > 0 || node?.type === "automate")
  const integritySource = useMemo(() => resolveNodeIntegrityValue(nodeStatus, node), [node, nodeStatus])
  const integrityValue = useMemo(() => parseIntegrityValue(integritySource), [integritySource])
  const integrityLabel = useMemo(() => formatIntegrityLabel(integrityValue), [integrityValue])
  const integrityTone = useMemo(() => getIntegrityTone(integrityValue), [integrityValue])
  const shouldRenderIntegrityBadge = !hasAutomate && Boolean(node?.medidor || integrityLabel)
  const integrityBadgeLabel = integrityLabel || "--"
  const integrityTooltipTitle = integrityLabel
    ? `Integridad de datos: ${integrityLabel}`
    : "Integridad de datos no disponible"
  const integrityColumnWidth = isCompact ? COMPACT_INTEGRITY_COLUMN_WIDTH : REGULAR_INTEGRITY_COLUMN_WIDTH
  const iconColumnWidth = isCompact ? COMPACT_ICON_COLUMN_WIDTH : REGULAR_ICON_COLUMN_WIDTH
  const actionColumnsTemplate = useMemo(() => {
    const columns = [
      integrityColumnWidth,
      iconColumnWidth,
      iconColumnWidth,
    ]

    if (!fromStateDashboard) columns.push(iconColumnWidth)

    columns.push(iconColumnWidth)

    return columns.join(" ")
  }, [fromStateDashboard, iconColumnWidth, integrityColumnWidth])
  const integrityColumnStyle = useMemo(
    () => ({
      width: integrityColumnWidth,
      minWidth: integrityColumnWidth,
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
    }),
    [integrityColumnWidth]
  )
  const iconColumnStyle = useMemo(
    () => ({
      width: iconColumnWidth,
      minWidth: iconColumnWidth,
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
    }),
    [iconColumnWidth]
  )
  const meterIconColumnStyle = useMemo(
    () => ({
      ...iconColumnStyle,
      marginRight: 0,
    }),
    [iconColumnStyle]
  )
  const arrowIconColumnStyle = useMemo(
    () => ({
      ...iconColumnStyle,
      marginLeft: isCompact ? "0.08rem" : "0.14rem",
      marginRight: isCompact ? "4rem" : "3rem",
    }),
    [iconColumnStyle, isCompact]
  )
  const actionIconButtonStyle = useMemo(
    () => ({
      padding: 0,
      minWidth: 0,
    }),
    []
  )

  const rowStyle = useMemo(() => {
    const baseRowStyle = getNodeRowStyle({ isSelected, isDisabled, depth })
    return {
      ...baseRowStyle,
      alignItems: isCompact ? "flex-start" : "center",
      padding: isCompact ? "0.38rem 0.55rem" : baseRowStyle.padding,
    }
  }, [depth, isCompact, isDisabled, isSelected])

  const nodeItemStyle = useMemo(
    () => ({
      ...getNodeItemStyle({ isSelected, depth, hasChildren }),
      ...rowStyle,
    }),
    [depth, hasChildren, isSelected, rowStyle]
  )

  const guideGutterWidth = depth * TREE_INDENT_SIZE
  const treeGuideAnchorX = isCompact ? TREE_GUIDE_ANCHOR_X_COMPACT : TREE_GUIDE_ANCHOR_X

  const handleRowClick = useCallback(() => {
    if (isDisabled) return
    handleSelectedNode(node)
  }, [handleSelectedNode, isDisabled, node])

  const handleArrowClick = useCallback((event) => {
    event.stopPropagation()
    onToggleExpanded(node?.id)
  }, [node?.id, onToggleExpanded])

  return (
    <Box
      style={{
        position: "relative",
        width: "100%",
        paddingLeft: `${guideGutterWidth}px`,
        boxSizing: "border-box",
      }}
    >
      {ancestorHasNext.slice(1).map((ancestorKeepsBranchOpen, index) =>
        ancestorKeepsBranchOpen ? (
          <Box
            key={`ancestor-guide-${node?.id}-${index}`}
            aria-hidden
            style={{
              position: "absolute",
              left: `${index * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: `-${TREE_GUIDE_BLEED}px`,
              bottom: `-${TREE_GUIDE_BLEED}px`,
              width: "1px",
              backgroundColor: stateTreeGuideColor,
              pointerEvents: "none",
            }}
          />
        ) : null
      )}

      {depth > 0 && (
        <>
          <Box
            aria-hidden
            style={{
              position: "absolute",
              left: `${(depth - 1) * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: `-${TREE_GUIDE_BLEED}px`,
              bottom: hasNextSibling ? `-${TREE_GUIDE_BLEED}px` : "50%",
              width: "1px",
              backgroundColor: stateTreeGuideColor,
              pointerEvents: "none",
            }}
          />

          <Box
            aria-hidden
            style={{
              position: "absolute",
              left: `${(depth - 1) * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: "50%",
              width: `${TREE_INDENT_SIZE - treeGuideAnchorX}px`,
              height: "1px",
              backgroundColor: stateTreeGuideColor,
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
        </>
      )}

      {hasChildren && isExpanded && (
        <Box
          aria-hidden
          style={{
            position: "absolute",
            left: `${depth * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
            top: "calc(100% - 1px)",
            bottom: `-${TREE_GUIDE_BLEED}px`,
            width: "1px",
            backgroundColor: stateTreeGuideColor,
            pointerEvents: "none",
          }}
        />
      )}

      <ListItem
        component="div"
        button
        selected={isSelected}
        onClick={handleRowClick}
        aria-disabled={isDisabled}
        style={nodeItemStyle}
      >
        <Box
          style={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: isCompact ? "flex-start" : "center",
            gap: isCompact ? "0.4rem" : "0.55rem",
            overflow: "hidden",
          }}
        >
          {!node?.owner && (
            <Tooltip title={`El nodo ${nodeLabel} es compartido`}>
              <IconButton
                edge="end"
                aria-label="nodo compartido"
                size="small"
                style={{ ...stateTreeActionButtonStyle, padding: 0 }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box
            style={{
              flexShrink: 0,
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: node?.color || themePalette.primary.main,
              border: `2px solid ${themePalette.common.white}`,
              boxShadow: `0 0 0 1px ${themePalette.border.main}, 0 3px 8px rgba(15, 23, 42, 0.08)`,
            }}
          />

          {!hideHierarchyBadge && (
            <span
              style={{
                ...stateTreeBadgeStyle,
                fontSize: isCompact ? "0.68rem" : stateTreeBadgeStyle.fontSize,
                padding: isCompact ? "0.1rem 0.35rem" : stateTreeBadgeStyle.padding,
                backgroundColor:
                  levelName === "empresa" ? "rgba(8, 95, 145, 0.08)" : stateTreeBadgeStyle.backgroundColor,
                borderColor:
                  levelName === "empresa" ? "rgba(8, 95, 145, 0.18)" : stateTreeBadgeStyle.borderColor,
                color: levelName === "empresa" ? themePalette.text.section : stateTreeBadgeStyle.color,
              }}
            >
              {badgeLabel}
            </span>
          )}

          <Box style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: "0.35rem", flexWrap: "wrap", overflow: "hidden" }}>
            <span
              style={{
                fontSize: isCompact ? "0.88rem" : depth === 0 ? "1rem" : "0.96rem",
                fontWeight: depth === 0 ? 500 : 400,
                color: themePalette.primary.main,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeLabel}
            </span>
          </Box>
        </Box>

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: actionColumnsTemplate,
            alignItems: "center",
            columnGap: 0,
            flexShrink: 0,
            paddingTop: isCompact ? 2 : 0,
            paddingRight: isCompact ? "0.08rem" : "0.14rem",
          }}
        >
          <Box style={integrityColumnStyle}>
            {shouldRenderIntegrityBadge && (
              <Tooltip title={integrityTooltipTitle}>
                <Box
                  component="span"
                  aria-label={`integridad de datos ${integrityBadgeLabel}`}
                  style={buildStateIntegrityBadgeStyle({ tone: integrityTone, isCompact })}
                >
                  {integrityBadgeLabel}
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box style={meterIconColumnStyle}>
            {node?.medidor ? (
              <Tooltip title={`${nodeLabel} tiene un medidor asociado ${statusNode ? "online" : "offline"}`}>
                <IconButton edge="end" aria-label="medidores asociados" size="small" style={actionIconButtonStyle}>
                  <SpeedIcon
                    fontSize="small"
                    style={{ color: statusNode ? themePalette.status.online : themePalette.status.offline }}
                  />
                </IconButton>
              </Tooltip>
            ) : node?.automates?.length > 0 ? (
              <Tooltip title={`${nodeLabel} tiene un automate asociado`}>
                <IconButton edge="end" aria-label="automates asociados" size="small" style={actionIconButtonStyle}>
                  <PrecisionManufacturingIcon
                    fontSize="small"
                    style={{ color: themePalette.text.disabled }}
                  />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>

          <Box style={iconColumnStyle}>
            {nodeStatus?.rssi === "-10dB" && (
              <Tooltip title={`${nodeLabel} tiene excelente senal de WiFi`}>
                <IconButton
                  edge="end"
                  aria-label="la senal de wifi es excelente"
                  size="small"
                  style={{ ...actionIconButtonStyle, color: themePalette.primary.main }}
                >
                  <WifiIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {!fromStateDashboard && (
            <Box style={iconColumnStyle}>
              <IconButton
                aria-label="eliminar franja horaria"
                onClick={() => handleDeleteNode?.(node)}
                size="small"
                style={{ ...actionIconButtonStyle, color: themePalette.error.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Box style={arrowIconColumnStyle}>
            {hasChildren &&
              (isExpanded ? (
                <IconButton edge="end" aria-label="contraer" onClick={handleArrowClick} size="small" style={actionIconButtonStyle}>
                  <ExpandLessIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton edge="end" aria-label="expandir" onClick={handleArrowClick} size="small" style={actionIconButtonStyle}>
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              ))}
          </Box>
        </Box>
      </ListItem>
    </Box>
  )
}

const areStateTreeRowPropsEqual = (prevProps, nextProps) => {
  const previousIsSelected = prevProps.selectedNodeId === prevProps.item?.node?.id
  const nextIsSelected = nextProps.selectedNodeId === nextProps.item?.node?.id

  return (
    prevProps.item === nextProps.item &&
    previousIsSelected === nextIsSelected &&
    prevProps.handleSelectedNode === nextProps.handleSelectedNode &&
    prevProps.statusByNodeId === nextProps.statusByNodeId &&
    prevProps.statusNodesByNodeId === nextProps.statusNodesByNodeId &&
    prevProps.fromStateDashboard === nextProps.fromStateDashboard &&
    prevProps.handleDeleteNode === nextProps.handleDeleteNode &&
    prevProps.isCompact === nextProps.isCompact &&
    prevProps.onToggleExpanded === nextProps.onToggleExpanded
  )
}

export default memo(StateTreeRow, areStateTreeRowPropsEqual)
